import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';

import { HttpErrorResponse } from '@angular/common/http';
import {
    AddCertifications,
    CreateCertification,
    CreateCertificationFailure,
    CreateCertificationSuccess,
    CreateInternalCertification,
    CreateInternalCertificationFailure,
    CreateInternalCertificationSuccess,
    DeleteCertification,
    DeleteCertificationFailure,
    DeleteCertificationSuccess,
    FetchCertification,
    FetchCertificationFailure,
    FetchCertificationSuccess,
    MoveCertification,
    MoveCertificationFailure,
    MoveCertificationSuccess,
    SetCertification,
    SetCertifications,
    UnsetCertification,
    UpdateCertification,
    UpdateCertificationFailure,
    UpdateCertificationStatus,
    UpdateCertificationStatusFailure,
    UpdateCertificationStatusSuccess,
    UpdateCertificationSuccess,
} from '../actions';
import { CertificationApiService } from '../services/certification.api-service';
import { Certification } from './certification';
import { CertificationStateModel } from './certification.state-model';

@State<CertificationStateModel>({
    name: 'certification',
    defaults: new CertificationStateModel(),
})
@Injectable()
export class CertificationState extends EntityState<Certification> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: CertificationStateModel, id: string = state.item): Certification {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: CertificationStateModel): Certification[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(CertificationState.sortProperties));

        return toReturn;
    }

    constructor(private _certificationService: CertificationApiService) {
        super(null, 30);
    }

    @Action(AddCertifications)
    addEntities(ctx: StateContext<CertificationStateModel>, action: AddCertifications): void {
        this._addEntities(ctx, action);
    }

    @Action(SetCertification)
    setEntity(ctx: StateContext<CertificationStateModel>, action: SetCertification): void {
        this._setEntity(ctx, action, AddCertifications);
    }

    @Action(UnsetCertification)
    unsetEntity(ctx: StateContext<CertificationStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateCertification)
    createEntity(ctx: StateContext<CertificationStateModel>, action: CreateCertification): void {
        this._certificationService.create(action.payload.name, action.payload.providerId, action.payload.hasExpirationDate, action.payload.targetCount).subscribe(
            (entity) => {
                ctx.dispatch([new AddCertifications([entity]), new CreateCertificationSuccess(entity)]);
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(
                    new CreateCertificationFailure({
                        response: error,
                    })
                );
            }
        );
    }

    @Action(MoveCertification)
    moveCertification(ctx: StateContext<CertificationStateModel>, action: MoveCertification): void {
        this._certificationService.move(action.payload.certificationId, action.payload.providerId).subscribe(
            (entity) => {
                ctx.dispatch(new MoveCertificationSuccess(entity));
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(
                    new MoveCertificationFailure({
                        response: error,
                    })
                );
            }
        );
    }

    @Action(CreateInternalCertification)
    createInternalCertification(ctx: StateContext<CertificationStateModel>, action: CreateInternalCertification): void {
        this._certificationService
            .createInternal(
                action.payload.name,
                action.payload.providerId,
                action.payload.validityPeriod,
                action.payload.file,
                action.payload.hasExpirationDate,
                action.payload.targetCount
            )
            .subscribe(
                (entity) => {
                    ctx.dispatch([new AddCertifications([entity]), new CreateInternalCertificationSuccess(entity)]);
                },
                (error: HttpErrorResponse) => {
                    ctx.dispatch(
                        new CreateInternalCertificationFailure({
                            response: error,
                        })
                    );
                }
            );
    }

    @Action(UpdateCertification)
    updateEntity(ctx: StateContext<CertificationStateModel>, action: UpdateCertification): void {
        this._certificationService
            .update(action.payload.id, action.payload.name, action.payload.validityPeriod, action.payload.hasExpirationDate, action.payload.targetCount)
            .subscribe(
                (entity) => {
                    ctx.dispatch([new AddCertifications([entity]), new UpdateCertificationSuccess(entity)]);
                },
                (error: HttpErrorResponse) => {
                    ctx.dispatch(new UpdateCertificationFailure({ response: error }));
                }
            );
    }

    @Action(DeleteCertification)
    deleteEntity(ctx: StateContext<CertificationStateModel>, action: DeleteCertification): void {
        this._certificationService.delete(action.payload).subscribe(
            () => {
                ctx.dispatch(new DeleteCertificationSuccess(action.payload));
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new DeleteCertificationFailure(action.payload));
            }
        );
    }

    @Action(UpdateCertificationStatus)
    updateCertificationStatus(ctx: StateContext<CertificationStateModel>, action: UpdateCertificationStatus): void {
        this._certificationService.updateStatus(action.payload.id, action.payload.status).subscribe(
            (entity) => {
                ctx.dispatch([new AddCertifications([entity]), new UpdateCertificationStatusSuccess(entity)]);
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new UpdateCertificationStatusFailure());
            }
        );
    }

    @Action(FetchCertification)
    fetchEntity(ctx: StateContext<CertificationStateModel>, action: FetchCertification): void {
        this._fetchEntity(ctx, action, FetchCertificationSuccess, FetchCertificationFailure, AddCertifications, SetCertification).then(() => {});
    }

    @Action(FetchCertificationSuccess)
    fetchEntitySuccess(ctx: StateContext<CertificationStateModel>, action: FetchCertificationSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchCertificationFailure)
    fetchEntityFailure(ctx: StateContext<CertificationStateModel>, action: FetchCertificationFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetCertifications)
    setEntities(ctx: StateContext<CertificationStateModel>, action: SetCertifications): void {
        this._setEntities(ctx, action);
    }
}
